import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaRadioGroupField, type RadioOption } from "@/shared/components/forms/ColocaRadioGroupField";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

import type { WalletDebitSendPreviewState } from "../hooks/useWalletDebitSendPreview";
import type { WalletDebitSendCorridor } from "../types/walletDebits.types";
import { corridorValue, OTHER_CORRIDOR } from "../utils/walletDebit";

/**
 * Where the manual send went, chosen among the rails the currency pays out on, each with what to send by hand
 * for the typed amount; or some other way, which pays no fees. The form field is `corridor`.
 */
export function WalletDebitCorridorField({
  state,
  currency,
}: {
  state: WalletDebitSendPreviewState;
  currency: string;
}) {
  const { t } = useTranslations("walletDebits");
  const corridors = state.status === "ready" ? state.preview.corridors : [];
  const showCountry = new Set(corridors.map((corridor) => corridor.country)).size > 1;
  const items: RadioOption[] = [
    ...corridors.map((corridor) => ({
      value: corridorValue(corridor),
      disabled: !corridor.net_amount,
      label: <CorridorLabel corridor={corridor} currency={currency} showCountry={showCountry} />,
    })),
    { value: OTHER_CORRIDOR, label: <OtherLabel /> },
  ];

  return (
    <div className="space-y-2 rounded-xl border border-border bg-muted/30 px-4 py-3">
      <ColocaRadioGroupField
        name="corridor"
        label={t("field_corridor")}
        description={t("send_preview_hint")}
        required
        items={items}
      />
      {state.status === "idle" && (
        <p className="text-xs text-muted-foreground">{t("send_preview_needs_amount")}</p>
      )}
      {state.status === "loading" && (
        <div className="space-y-1.5" aria-busy="true">
          <ColocaSkeleton className="h-4 w-2/3" />
          <ColocaSkeleton className="h-3 w-1/2" />
        </div>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-xs text-destructive">
          {t("send_preview_failed", { message: state.error })}
        </p>
      )}
      {state.status === "ready" && corridors.length === 0 && (
        <p className="text-xs text-muted-foreground">{t("send_preview_no_corridors", { currency })}</p>
      )}
    </div>
  );
}

function CorridorLabel({
  corridor,
  currency,
  showCountry,
}: {
  corridor: WalletDebitSendCorridor;
  currency: string;
  showCountry: boolean;
}) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const money = (value: string) => `${formatCurrencyAmount(value, currency, activeLanguage)} ${currency}`;
  const rail = showCountry ? `${formatRail(corridor.rail)} · ${corridor.country}` : formatRail(corridor.rail);

  if (!corridor.net_amount) {
    return (
      <span className="text-muted-foreground">
        <span className="font-medium">{rail}</span>
        {" · "}
        {t(
          corridor.unpriced_reason === "amount_below_fees"
            ? "send_preview_below_fees"
            : "send_preview_no_fee_configured",
        )}
      </span>
    );
  }

  const taxNames = corridor.taxes.map((tax) => tax.name).join(", ");
  return (
    <span className="block min-w-0 leading-snug">
      <span className="block text-foreground">
        <span className="font-medium">{rail}</span>
        {" · "}
        {t("send_preview_send")}{" "}
        <span className="font-semibold tabular-nums">{money(corridor.net_amount)}</span>
      </span>
      <span className="mt-0.5 block text-xs text-muted-foreground tabular-nums">
        {t("send_preview_fees", {
          commission: money(corridor.commission ?? "0"),
          taxes: money(corridor.tax_total ?? "0"),
        })}
        {taxNames && ` (${taxNames})`}
      </span>
    </span>
  );
}

function OtherLabel() {
  const { t } = useTranslations("walletDebits");
  return (
    <span className="block leading-snug">
      <span className="block font-medium text-foreground">{t("corridor_other")}</span>
      <span className="mt-0.5 block text-xs text-muted-foreground">{t("corridor_other_hint")}</span>
    </span>
  );
}
